/**
 * BikeGhostRaceMap — Leaflet map with two animated riders + gap HUD.
 *
 * Adapted from running/comparison/GhostRaceMap — no checkpoint markers.
 */

import { useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Box, Typography, useTheme } from '@mui/material';
import { tokens } from '../../../theme';

const COLOR_A = '#3b82f6';
const COLOR_B = '#f59e0b';
const TRAIL_LEN = 20;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

function formatHms(seconds) {
  if (!seconds) return '0:00:00';
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
}

function formatKm(m) {
  if (m == null) return '—';
  return `${(m / 1000).toFixed(1)} km`;
}

function formatTimeDelta(s) {
  if (s == null) return '—';
  const sign = s < 0 ? '−' : '+';
  const abs = Math.abs(s);
  const m = Math.floor(abs / 60);
  const sec = Math.floor(abs % 60);
  return `${sign}${m}:${sec.toString().padStart(2, '0')}`;
}

function binarySearchElapsed(resampled, T, key = 'elapsed_a_s') {
  let lo = 0, hi = resampled.length - 1;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (resampled[mid][key] < T) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

function chunkLatLon(chunk) {
  if (!chunk) return null;
  if (chunk.points?.length) {
    const pt = chunk.points[0];
    if (pt.lat != null && pt.lon != null) return [pt.lat, pt.lon];
  }
  if (chunk.lat != null && chunk.lon != null) return [chunk.lat, chunk.lon];
  return null;
}

function buildTrack(analysis) {
  if (!analysis?.chunks) return [];
  const track = [];
  for (const chunk of analysis.chunks) {
    const pos = chunkLatLon(chunk);
    if (pos) track.push(pos);
  }
  return track;
}

function getPosAtTime(analysis, resampled, T, elapsedKey) {
  if (!resampled.length || !analysis?.chunks) return null;
  const idx = binarySearchElapsed(resampled, T, elapsedKey);
  const row = resampled[Math.min(idx, resampled.length - 1)];
  const chunkIdx = Math.round((row.course_distance_m || 0) / 100);
  const chunks = analysis.chunks;
  return chunkLatLon(chunks[Math.min(chunkIdx, chunks.length - 1)]);
}

function SetupPanes() {
  const map = useMap();
  useEffect(() => {
    if (!map.getPane('ghostPane')) {
      const p = map.createPane('ghostPane');
      p.style.zIndex = 650;
    }
  }, [map]);
  return null;
}

function GhostDots({ analysisA, analysisB, resampled, scrubberTime }) {
  const map = useMap();
  const markerARef = useRef(null);
  const markerBRef = useRef(null);
  const trailARef = useRef(null);
  const trailBRef = useRef(null);
  const trailAPoints = useRef([]);
  const trailBPoints = useRef([]);

  useEffect(() => {
    markerARef.current = L.circleMarker([0, 0], {
      radius: 10, color: '#fff', fillColor: COLOR_A, fillOpacity: 0.9, weight: 2, pane: 'ghostPane',
    }).addTo(map);
    markerBRef.current = L.circleMarker([0, 0], {
      radius: 10, color: '#fff', fillColor: COLOR_B, fillOpacity: 0.9, weight: 2, pane: 'ghostPane',
    }).addTo(map);
    trailARef.current = L.polyline([], { color: COLOR_A, opacity: 0.45, weight: 3, dashArray: '4 4', pane: 'ghostPane' }).addTo(map);
    trailBRef.current = L.polyline([], { color: COLOR_B, opacity: 0.45, weight: 3, dashArray: '4 4', pane: 'ghostPane' }).addTo(map);
    return () => {
      markerARef.current?.remove();
      markerBRef.current?.remove();
      trailARef.current?.remove();
      trailBRef.current?.remove();
    };
  }, [map]);

  useEffect(() => {
    const posA = getPosAtTime(analysisA, resampled, scrubberTime, 'elapsed_a_s');
    const posB = getPosAtTime(analysisB, resampled, scrubberTime, 'elapsed_b_s');
    if (posA) {
      markerARef.current?.setLatLng(posA);
      trailAPoints.current = [...trailAPoints.current.slice(-TRAIL_LEN), posA];
      trailARef.current?.setLatLngs(trailAPoints.current);
    }
    if (posB) {
      markerBRef.current?.setLatLng(posB);
      trailBPoints.current = [...trailBPoints.current.slice(-TRAIL_LEN), posB];
      trailBRef.current?.setLatLngs(trailBPoints.current);
    }
  }, [scrubberTime, analysisA, analysisB, resampled]);

  return null;
}

function GapHud({ resampled, scrubberTime }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const row = useMemo(() => {
    if (!resampled.length) return null;
    const idx = binarySearchElapsed(resampled, scrubberTime, 'elapsed_a_s');
    return resampled[Math.min(idx, resampled.length - 1)];
  }, [resampled, scrubberTime]);

  if (!row) return null;

  return (
    <Box sx={{
      position: 'absolute', top: 10, right: 10, zIndex: 1000,
      backgroundColor: 'rgba(15,23,42,0.85)',
      border: '1px solid rgba(255,255,255,0.15)',
      borderRadius: 1.5, px: 1.5, py: 1, minWidth: 150, pointerEvents: 'none',
    }}>
      <Typography variant="caption" fontWeight="bold" color="#fff" display="block" mb={0.5}>
        ⏱ {formatHms(scrubberTime)}
      </Typography>
      <Typography variant="caption" color={COLOR_A} display="block">A  {formatKm(row.course_distance_m)}</Typography>
      <Typography variant="caption" color={COLOR_B} display="block">B  {formatKm(row.course_distance_m)}</Typography>
      <Box mt={0.5} pt={0.5} sx={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <Typography variant="caption" color={row.time_delta_s >= 0 ? '#22c55e' : '#ef4444'} display="block">
          Gap A {formatTimeDelta(-row.time_delta_s)}
        </Typography>
      </Box>
    </Box>
  );
}

export default function BikeGhostRaceMap({ analysisA, analysisB, resampled = [], scrubberTime }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const trackA = useMemo(() => buildTrack(analysisA), [analysisA]);
  const trackB = useMemo(() => buildTrack(analysisB), [analysisB]);

  const center = useMemo(() => {
    if (trackA.length) return trackA[Math.floor(trackA.length / 2)];
    if (trackB.length) return trackB[Math.floor(trackB.length / 2)];
    return [46.0, 7.0];
  }, [trackA, trackB]);

  if (!trackA.length && !trackB.length) {
    return (
      <Box p={3} textAlign="center">
        <Typography color={colors.grey[500]}>No GPS data available for map</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', height: 420, borderRadius: 2, overflow: 'hidden' }}>
      <MapContainer center={center} zoom={10} style={{ height: '100%', width: '100%' }} zoomControl>
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        {trackA.length > 0 && (
          <Polyline positions={trackA} pathOptions={{ color: COLOR_A, opacity: 0.5, weight: 2 }} />
        )}
        {trackB.length > 0 && (
          <Polyline positions={trackB} pathOptions={{ color: COLOR_B, opacity: 0.5, weight: 2, dashArray: '6 4' }} />
        )}
        <SetupPanes />
        <GhostDots analysisA={analysisA} analysisB={analysisB} resampled={resampled} scrubberTime={scrubberTime} />
      </MapContainer>
      <GapHud resampled={resampled} scrubberTime={scrubberTime} />
    </Box>
  );
}
